import type { AstroSystem, HoroscopePeriod } from '@astrally/shared';
import type { HoroscopeResult, Panchanga } from '@astrally/astro-engine';
import { SYSTEM_LABELS } from './horoscopeData';

/**
 * Every word the horoscope screen says that is not already in the engine's
 * output or in app/src/i18n/en.ts.
 *
 * WHY IT LIVES HERE, not in en.ts: the house rule is that long-form content
 * belongs to its feature's data file. That keeps the dictionary a dictionary
 * and lets this copy be read as one piece — which matters here, because this
 * screen's whole job is explaining two systems to somebody who has never met
 * either, and copy that is only legible as forty scattered keys drifts.
 *
 * KNOWN GAP, stated rather than hidden: strings in this file do not translate.
 * The keys that already exist (`panchanga.*`, `home.moonUncertain`,
 * `horoscope.quietDay`, `safety.disclaimer`, …) are used through `t()` and do
 * translate. When the horoscope screen is next opened for localisation, the
 * short headings below should move into en.ts under a `horoscope.*` prefix and
 * only the paragraphs should stay.
 *
 * Copy rules, same as everywhere: astrology is a lens, never proof and never a
 * prediction of events. Nothing here promises an outcome.
 */

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

export const PERIOD_GROUP_LABEL = 'How far ahead to read';
export const SYSTEM_GROUP_LABEL = 'Which tradition to read in';

/** The third option on the system control. The two real ones come from SYSTEM_LABELS. */
export const BOTH_LABEL = 'Both';

export function systemName(system: AstroSystem): string {
  return SYSTEM_LABELS[system].name;
}

/** One line under the system control saying what the reader just chose. */
export const SYSTEM_BLURB: Record<AstroSystem | 'both', string> = {
  vedic:
    'Jyotish — Indian astrology. Read from your Chandra rashi, the rashi your Moon held when you were born, on the sidereal zodiac, which is measured against the actual fixed stars.',
  western:
    "Western astrology. Read from today's planets meeting your whole birth chart, on the tropical zodiac, which is measured from the March equinox.",
  both: 'Both traditions, side by side — including where they disagree, and why.',
};

// ---------------------------------------------------------------------------
// Section headings
// ---------------------------------------------------------------------------

export const SECTION_READING = 'Your reading';
export const SECTION_DIVERGENCE = 'Where the two systems land';
export const SECTION_PANCHANGA = "Today's panchanga";
export const SECTION_NARRATION = 'Put it together for me';

export const MORE_SIGNALS = 'Everything else this period';
export const SHOW_MORE = 'Show all';
export const SHOW_LESS = 'Show fewer';

// ---------------------------------------------------------------------------
// Where the reading is cast
// ---------------------------------------------------------------------------

export const CAST_FOR = 'Cast for';

/**
 * Astrally holds exactly one coordinate — the birth place — because it never
 * asks the device where you are. Say so instead of quietly showing someone in
 * London a Kolkata sunrise and letting them think the app is broken.
 */
export function movedAwayNote(placeLabel: string | undefined, deviceZone: string): string {
  const where = placeLabel ? `your birth place (${placeLabel})` : 'your birth place';
  return `Your device clock is on ${deviceZone}, and this reading is cast for ${where} — the only location Astrally holds, because it never asks your phone where you are. Sunrise and the day's windows below are that city's, not the one you are standing in.`;
}

// ---------------------------------------------------------------------------
// The five limbs, and the day's windows
// ---------------------------------------------------------------------------

export const PANCHANGA_INTRO =
  'Panchanga means "five limbs" — the five measures a traditional Indian almanac prints for any given day. They are not about you; they describe the day itself, the same for everyone in this city.';

export const LIMB_UNTIL = 'until';

export const PAKSHA_SHUKLA =
  'Shukla paksha — the fortnight running from new Moon to full, read as the growing half of the month.';
export const PAKSHA_KRISHNA =
  'Krishna paksha — the fortnight running from full Moon to new, read as the winding-down half of the month.';

export const VISHTI_NOTE =
  'Vishti (also called Bhadra) is running — the one karana tradition says to postpone starting anything new past.';

export const WINDOWS_TITLE = "Today's windows";
export const WINDOWS_INTRO =
  'Both windows are fractions of the real daylight at this place, so they move with the season and with the city. They are conventions about when to begin things, not claims that something will happen.';

export const NO_ABHIJIT =
  'No abhijit muhurta today. Wednesday is the one weekday the tradition grants none, so the usual all-purpose good window simply is not available.';

export const AVOID_WORD = 'Traditionally avoided';
export const FAVOURED_WORD = 'Traditionally favoured';
export const DAYLIGHT_WORD = 'Daylight';
export const NOW_WORD = 'Now';

export const SUNRISE_ESTIMATED =
  'The Sun does not properly rise or set at this latitude on this date, so 6 am and 6 pm local were substituted to divide the day. Treat these windows as indicative only.';

// ---------------------------------------------------------------------------
// Rating
// ---------------------------------------------------------------------------

export const RATE_PROMPT = 'Did this land?';
export const RATE_HELP =
  'Your answers stay on this device and build up a record of how often each lens felt true to you. That is a record of your experience — never evidence that astrology predicts anything.';
export const RATE_ARIA = 'Rate this signal';

// ---------------------------------------------------------------------------
// Divergence — the "Both" view's whole reason to exist
// ---------------------------------------------------------------------------

export const VERDICT_LABEL: Record<'agree' | 'overlap' | 'diverge', string> = {
  agree: 'They agree this period',
  overlap: 'They partly overlap',
  diverge: 'They point different ways',
};

export const SUN_SPLIT_TITLE = 'Your Sun, in both zodiacs';

/**
 * `sidereal` arrives as a rashi name (Karka…) and `tropical` as a Western sign
 * name (Cancer…) — each half of the card speaks its own tradition's language,
 * which is the point. `differs` is computed from sign INDICES in compare.ts,
 * because the two labels never match as strings even when the sign is the same.
 */
export function sunSplitLine(sidereal: string, tropical: string, differs: boolean): string {
  if (!differs) {
    return `Both zodiacs currently put your Surya — your Sun — in the same sign: Jyotish calls it ${sidereal}, Western calls it ${tropical}. That happens when your birth degree sits far enough from a sign boundary that the gap between the two does not push it across — most people land in two different signs, you do not.`;
  }
  return `Jyotish reads your Surya in ${sidereal}. Western reads your Sun in ${tropical}. Neither is a mistake: they are counting from different starting points, so the same sky gets two different labels.`;
}

export const SHARED_AREAS = 'Both flagged';
export const VEDIC_ONLY = 'Only Jyotish flagged';
export const WESTERN_ONLY = 'Only Western flagged';

export const WHICH_IS_RIGHT =
  'The honest answer to "so which one is right?" is that they are measuring different things. Astrally shows both and lets you decide what fits — which is also what the rate buttons are for.';

// ---------------------------------------------------------------------------
// Failure states
// ---------------------------------------------------------------------------

export const NO_CHART_CTA = 'Add your birth details';

export const ENGINE_ERROR_HINT = 'Try another period, or reopen this screen.';

/**
 * Offered beside the uncertain-Moon refusal. The Western reading is genuinely
 * unaffected — it tracks transits against the whole natal chart rather than
 * counting houses from one sign — so the reader is not left with nothing.
 */
export const SEE_WESTERN = 'See the Western reading instead';

// ---------------------------------------------------------------------------
// AI narration — the only thing on this screen that costs anything
// ---------------------------------------------------------------------------

export const NARRATE_HINT =
  'Everything above is already calculated and free to reopen. Ally can read it back as one piece — that spends one of your daily messages.';

export const NARRATE_CTA = 'Read this period for me';

export const NARRATE_CACHED = 'Saved for today. Reopening this screen does not spend another message.';

/**
 * Answers a tap on the narration button while a generation is already running.
 * The button stays pressable — a dead button reads as a broken app, and this
 * says what is actually happening instead of nothing at all.
 */
export const NARRATE_BUSY =
  'Astrally is already answering something. Give that a moment and press this again.';

const PERIOD_PHRASE: Record<HoroscopePeriod, string> = {
  day: 'today',
  week: 'this week',
  month: 'this month',
  year: 'this year',
};

/**
 * The AI's job here is to NARRATE what the engine already produced. The prompt
 * therefore carries the composed headline and the exact signals and asks for
 * nothing beyond them — it never asks the model where a planet is.
 */
export function narrationPrompt(
  readings: HoroscopeResult[],
  period: HoroscopePeriod,
  panchanga: Panchanga | null,
): string {
  const parts = readings.map((r) => {
    const lines = r.strongest
      .slice(0, 4)
      .map((s) => `- ${s.label}: ${s.meaning}`)
      .join('\n');
    return `${r.system === 'vedic' ? 'Jyotish' : 'Western'} reading — ${r.headline}\n${lines}`;
  });
  const day =
    panchanga && period === 'day'
      ? `\n\nToday's panchanga: ${panchanga.tithi.name} tithi (${panchanga.tithi.paksha}), ${panchanga.nakshatra.name} nakshatra, ${panchanga.yoga.name} yoga, ${panchanga.karana.name} karana, ${panchanga.vaara.englishName}.`
      : '';
  const both =
    readings.length > 1
      ? ' Where the two traditions point at different things, say so plainly rather than blending them into one answer.'
      : '';
  return (
    `Read ${PERIOD_PHRASE[period]} for me. Astrally has already calculated everything below — ` +
    'put it together in your own words for someone with no astrology background, say what it ' +
    'MEANS rather than what will happen, and do not add any placement I have not given you.' +
    both +
    '\n\n' +
    parts.join('\n\n') +
    day
  );
}
